import { PageCtas } from '@/Components/Main/PageCtas';
import TestimonialsSection from '@/Components/Main/TestimonialsSection';
import VideoPlayer from '@/Components/Main/VideoPlayer';
import { Button } from '@/Components/ui/button';
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/Components/ui/card';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/Components/ui/carousel';
import { Dialog, DialogContent, DialogTrigger } from '@/Components/ui/dialog';
import useTrackVisit from '@/Hooks/useTrackVisit';
import Layout from '@/Layouts/MainLayout';
import { cn } from '@/lib/utils';
import { Head, Link } from '@inertiajs/react';
import axios from 'axios';
import Autoplay from 'embla-carousel-autoplay';
import { motion } from 'framer-motion';
import { ArrowRight, Cloud, Globe, Play, Sun } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface HomeProps {
    heroVideo: string;
    heroImages: string[];
    heroTitle: string;
    heroDescription: string;
    heroPrimaryCta: {
        text: string;
        link: string;
    };
    heroSecondaryCta: {
        text: string;
        link: string;
    };
    aboutTitle: string;
    aboutDescription: string;
    servicesTitle: string;
    servicesDescription: string;
    blogTitle: string;
    blogDescription: string;
    companyInfo?: {
        name: string;
        description: string;
        mission?: string;
        vision?: string;
    };
    whyChooseUs: Array<{
        title: string;
        description: string;
    }>;
    services: Array<{
        id: number;
        image: string;
        title: string;
        description: string;
        category: string;
    }>;
    blogPosts: Array<{
        id: number;
        slug: string;
        title: string;
        excerpt: string;
        author: {
            name: string;
            avatar: string;
            role: string;
        };
        date: string;
        image: string;
        category: string;
        readTime: string;
    }>;
    topCtas: Array<{
        type:
            | 'subscribe'
            | 'contact'
            | 'register'
            | 'apply'
            | 'demo'
            | 'learn-more';
        title: string;
        description: string;
        buttonText?: string;
        href?: string;
        background: 'green' | 'white' | 'gradient' | 'image';
        backgroundImage?: string;
        overlayColor?: string;
        placement: string;
    }>;
    middleCtas: Array<{
        type:
            | 'subscribe'
            | 'contact'
            | 'register'
            | 'apply'
            | 'demo'
            | 'learn-more';
        title: string;
        description: string;
        buttonText?: string;
        href?: string;
        background: 'green' | 'white' | 'gradient' | 'image';
        backgroundImage?: string;
        overlayColor?: string;
        placement: string;
    }>;
    bottomCtas: Array<{
        type:
            | 'subscribe'
            | 'contact'
            | 'register'
            | 'apply'
            | 'demo'
            | 'learn-more';
        title: string;
        description: string;
        buttonText?: string;
        href?: string;
        background: 'green' | 'white' | 'gradient' | 'image';
        backgroundImage?: string;
        overlayColor?: string;
        placement: string;
    }>;
    stats: Array<{
        stat: string;
        label: string;
        description: string;
    }>;
}

const Section: React.FC<{
    children: React.ReactNode;
    className?: string;
    containerClassName?: string;
}> = ({ children, className, containerClassName }) => (
    <section className={cn('py-20', className)}>
        <div className={cn('container mx-auto px-4', containerClassName)}>
            {children}
        </div>
    </section>
);

const Home: React.FC<HomeProps> = ({
    heroVideo,
    heroImages,
    heroTitle,
    heroDescription,
    heroPrimaryCta,
    heroSecondaryCta,
    aboutTitle,
    aboutDescription,
    servicesTitle,
    servicesDescription,
    blogTitle,
    blogDescription,
    companyInfo = {
        name: 'Gaphen Projects Limited',
        description: 'Revolutionizing African Agriculture',
    },
    whyChooseUs,
    services,
    blogPosts,
    topCtas,
    middleCtas,
    bottomCtas,
    stats,
}) => {
    useTrackVisit();
    const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                const response = await axios.get('/testimonials');
                setTestimonials(response.data);
            } catch (error) {
                console.error('Error fetching testimonials:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    return (
        <Layout>
            <Head title="Gaphen - Empowering African Farmers" />

            {/* Top CTAs */}
            <PageCtas ctas={topCtas} className="mb-8" />

            {/* Hero Section */}
            <Section className="relative min-h-[calc(100vh-8rem)] overflow-hidden bg-gradient-to-br from-emerald-50 via-amber-50 to-emerald-100 dark:from-emerald-900/50 dark:via-amber-900/50 dark:to-emerald-800/50">
                <div className="container relative mx-auto grid items-center gap-12 px-4 md:grid-cols-2">
                    {/* Hero Carousel */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="relative order-1"
                    >
                        <div className="absolute -inset-4 rounded-xl bg-emerald-600/10 blur-xl" />
                        <Carousel
                            plugins={[Autoplay({ delay: 3000 })]}
                            className="relative z-10 w-full max-w-xl"
                        >
                            <CarouselContent>
                                {heroImages.map((image, index) => (
                                    <CarouselItem key={index}>
                                        <div className="overflow-hidden rounded-xl border-2 border-white/50 p-1 backdrop-blur-sm dark:border-gray-700/50">
                                            <div className="relative overflow-hidden rounded-lg">
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                                <img
                                                    src={image}
                                                    alt={`Gaphen illustration ${index + 1}`}
                                                    className="h-[400px] w-full object-cover transition-transform duration-300 hover:scale-105 md:h-[500px]"
                                                />
                                            </div>
                                        </div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            <CarouselPrevious className="left-4 bg-white/80 text-emerald-600 hover:bg-white dark:bg-gray-800/80 dark:text-emerald-400 dark:hover:bg-gray-800" />
                            <CarouselNext className="right-4 bg-white/80 text-emerald-600 hover:bg-white dark:bg-gray-800/80 dark:text-emerald-400 dark:hover:bg-gray-800" />
                        </Carousel>
                    </motion.div>

                    {/* Hero Content */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="relative z-10 order-2 space-y-8 text-center md:text-left"
                    >
                        <div className="inline-flex items-center rounded-full bg-emerald-50/80 px-4 py-2 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                            <img
                                src="images/logo.png"
                                className="mr-2 h-6 w-auto"
                            />
                            Transforming African Agriculture
                        </div>

                        <h1 className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-4xl font-bold leading-tight text-transparent md:text-5xl lg:text-6xl">
                            {heroTitle}
                        </h1>

                        <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                            {heroDescription}
                        </p>

                        <div className="flex flex-col justify-center space-y-4 md:flex-row md:justify-start md:space-x-4 md:space-y-0">
                            <Link href={heroPrimaryCta.link}>
                                <Button
                                    size="lg"
                                    className="group relative w-full overflow-hidden bg-gradient-to-r from-emerald-600 to-amber-600 text-white transition-all hover:from-emerald-700 hover:to-amber-700 md:w-auto"
                                >
                                    <span className="relative z-10 flex items-center">
                                        {heroPrimaryCta.text}
                                        <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </Button>
                            </Link>

                            <Dialog>
                                <DialogTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="lg"
                                        className="group flex w-full items-center justify-center gap-2 border-emerald-200 bg-white/80 backdrop-blur-sm transition-all hover:bg-emerald-50 dark:border-emerald-800 dark:bg-gray-800/80 dark:hover:bg-emerald-900/30 md:w-auto"
                                    >
                                        <div className="rounded-full bg-emerald-50 p-2 transition-all group-hover:bg-emerald-100 dark:bg-emerald-900 dark:group-hover:bg-emerald-800">
                                            <Play className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        {heroSecondaryCta.text}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-4xl">
                                    <VideoPlayer src={heroVideo} />
                                </DialogContent>
                            </Dialog>
                        </div>
                    </motion.div>
                </div>
            </Section>

            {/* Middle CTAs */}
            <PageCtas ctas={middleCtas} className="my-12" />

            {/* About Section */}
            <Section className="relative overflow-hidden bg-gradient-to-br from-emerald-50/50 via-white to-amber-50/50 dark:from-emerald-900/30 dark:via-gray-900 dark:to-amber-900/30">
                <div className="container relative mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mx-auto max-w-3xl text-center"
                    >
                        <h2 className="mb-6 bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
                            {aboutTitle}
                        </h2>
                        <p className="mb-8 text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                            {aboutDescription}
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Button
                                asChild
                                variant="default"
                                className="group relative overflow-hidden bg-gradient-to-r from-emerald-600 to-amber-600 text-white transition-all hover:from-emerald-700 hover:to-amber-700"
                            >
                                <Link href="/about">
                                    <span className="relative z-10 flex items-center">
                                        Learn More About Us
                                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                className="group border-emerald-200 bg-white/80 text-emerald-600 backdrop-blur-sm transition-all hover:border-emerald-300 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-gray-800/80 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
                            >
                                <Link href="/contact">
                                    <span className="relative z-10 flex items-center">
                                        Contact Our Team
                                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </span>
                                </Link>
                            </Button>
                        </div>
                    </motion.div>

                    {/* Stats Section with Weather-Inspired Cards */}
                    <Section className="relative overflow-hidden bg-gradient-to-br from-emerald-50/50 via-white to-amber-50/50 dark:from-emerald-900/30 dark:via-gray-900 dark:to-amber-900/30">
                        <div className="container relative mx-auto px-4 text-center">
                            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-base font-medium text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                                <img
                                    src="images/logo.png"
                                    className="h-10 w-auto"
                                />
                                Gaphen in Numbers
                            </div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="grid gap-8 md:grid-cols-3"
                            >
                                {stats.map((item, index) => {
                                    const icons = [Globe, Sun, Cloud];
                                    const colors = [
                                        {
                                            text: 'text-emerald-600',
                                            bg: 'bg-emerald-50',
                                        },
                                        {
                                            text: 'text-amber-600',
                                            bg: 'bg-amber-50',
                                        },
                                        {
                                            text: 'text-blue-600',
                                            bg: 'bg-blue-50',
                                        },
                                    ];
                                    const Icon = icons[index % icons.length];
                                    const color = colors[index % colors.length];

                                    return (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{
                                                duration: 0.6,
                                                delay: 0.1 * index,
                                            }}
                                        >
                                            <Card className="group h-full border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-gray-700/50 dark:bg-gray-800/80">
                                                <CardContent className="p-6">
                                                    <div className="mb-4 flex items-center gap-4">
                                                        <div
                                                            className={cn(
                                                                'rounded-xl p-3',
                                                                color.bg,
                                                            )}
                                                        >
                                                            <Icon
                                                                className={cn(
                                                                    'h-6 w-6',
                                                                    color.text,
                                                                )}
                                                            />
                                                        </div>
                                                        <div>
                                                            <div className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-2xl font-bold text-transparent">
                                                                {item.stat}
                                                            </div>
                                                            <div className="font-medium text-gray-600 dark:text-gray-300">
                                                                {item.label}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <p className="text-gray-500 dark:text-gray-400">
                                                        {item.description}
                                                    </p>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                        </div>
                    </Section>

                    {/* Mission Statement */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mx-auto mt-16 max-w-2xl rounded-2xl border-2 border-white/50 bg-white/50 p-8 text-center backdrop-blur-sm dark:border-gray-700/50 dark:bg-gray-800/50"
                    >
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-base font-medium text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                            <img
                                src="images/logo.png"
                                className="h-10 w-auto"
                            />
                            Our Mission
                        </div>
                        <p className="text-lg font-medium text-gray-600 dark:text-gray-300">
                            {companyInfo.mission ||
                                'Empowering African farmers through innovative agricultural solutions, sustainable practices, and market access to create lasting positive impact in communities.'}
                        </p>
                    </motion.div>
                </div>
            </Section>

            {/* Why Choose Us Section */}
            <Section className="relative overflow-hidden bg-white dark:bg-gray-900">
                <div className="container relative mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mb-12 text-center"
                    >
                        <h2 className="mb-4 bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
                            Why Choose Us
                        </h2>
                        <p className="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
                            We're committed to transforming agriculture in
                            Africa
                        </p>
                    </motion.div>
                    <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
                        {whyChooseUs.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.6,
                                    delay: index * 0.1,
                                }}
                            >
                                <Card className="group h-full border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-gray-700/50 dark:bg-gray-800/80">
                                    <CardHeader>
                                        <CardTitle className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-transparent">
                                            {item.title}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            {item.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* Services Section with Enhanced Cards */}
            <Section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-amber-50/30 to-emerald-50 dark:from-emerald-900/50 dark:via-amber-900/30 dark:to-emerald-900/50">
                <div className="container relative mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mb-16 text-center"
                    >
                        <h2 className="mb-4 bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
                            {servicesTitle}
                        </h2>
                        <p className="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
                            {servicesDescription}
                        </p>
                    </motion.div>

                    <div className="grid gap-8 md:grid-cols-3">
                        {services.map((service) => (
                            <motion.div
                                key={service.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.6,
                                    delay: service.id * 0.1,
                                }}
                            >
                                <Card className="group h-full overflow-hidden border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700/50 dark:bg-gray-800/80">
                                    <div className="relative h-48 w-full overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                        <img
                                            src={service.image}
                                            alt={service.title}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                    </div>
                                    <CardHeader>
                                        <CardTitle className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-xl font-bold text-transparent">
                                            {service.title}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="relative space-y-4">
                                        <p className="text-gray-600 dark:text-gray-300">
                                            {service.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-16 text-center"
                    >
                        <Button
                            asChild
                            variant="outline"
                            className="group relative overflow-hidden border-emerald-200 bg-white/80 text-emerald-600 backdrop-blur-sm transition-all hover:border-emerald-300 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-gray-800/80 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
                        >
                            <Link href="/services">
                                <span className="relative z-10 flex items-center">
                                    View All Services
                                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </span>
                            </Link>
                        </Button>
                    </motion.div>
                </div>
            </Section>

            {/* Blog Section with Enhanced Cards */}
            <Section className="relative overflow-hidden bg-gradient-to-br from-emerald-50/50 via-white to-amber-50/50 dark:from-emerald-900/30 dark:via-gray-900 dark:to-amber-900/30">
                <div className="container relative mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mb-8 text-center"
                    >
                        <h2 className="mb-4 bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
                            {blogTitle}
                        </h2>
                        <p className="mx-auto max-w-2xl text-gray-600 dark:text-gray-300">
                            {blogDescription}
                        </p>
                    </motion.div>

                    <div className="grid gap-8 md:grid-cols-3">
                        {blogPosts.map((post) => (
                            <motion.div
                                key={post.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.6,
                                    delay: post.id * 0.1,
                                }}
                            >
                                <Card className="group h-full overflow-hidden border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700/50 dark:bg-gray-800/80">
                                    <div className="relative h-48 w-full overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                        <img
                                            src={post.image}
                                            alt={post.title}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                        <div className="absolute bottom-4 left-4 rounded-full bg-emerald-50/90 px-3 py-1 text-xs font-medium text-emerald-600 backdrop-blur-sm dark:bg-emerald-900/90 dark:text-emerald-400">
                                            {post.category}
                                        </div>
                                    </div>
                                    <CardHeader>
                                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                            <div className="flex items-center gap-2">
                                                <img
                                                    src={post.author.avatar}
                                                    alt={post.author.name}
                                                    className="h-6 w-6 rounded-full object-cover"
                                                />
                                                <span>{post.author.name}</span>
                                            </div>
                                            <span>·</span>
                                            <span>{post.readTime}</span>
                                        </div>
                                        <CardTitle className="group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                                            {post.title}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-gray-600 dark:text-gray-300">
                                            {post.excerpt.length > 300
                                                ? `${post.excerpt.substring(0, 300)}...`
                                                : post.excerpt}
                                        </p>
                                    </CardContent>
                                    <CardFooter>
                                        <Button
                                            asChild
                                            variant="ghost"
                                            className="group ml-auto text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
                                        >
                                            <Link href={`/blog/${post.slug}`}>
                                                Read More
                                                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                            </Link>
                                        </Button>
                                    </CardFooter>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-16 text-center"
                    >
                        <Button
                            asChild
                            variant="outline"
                            className="group relative overflow-hidden border-emerald-200 bg-white/80 text-emerald-600 backdrop-blur-sm transition-all hover:border-emerald-300 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-gray-800/80 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
                        >
                            <Link href="/blog">
                                <span className="relative z-10 flex items-center">
                                    View All Posts
                                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </span>
                            </Link>
                        </Button>
                    </motion.div>
                </div>
            </Section>

            {/* Testimonials Section */}
            {!loading && testimonials.length > 0 && (
                <Section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-amber-50 to-emerald-100 dark:from-emerald-900/50 dark:via-amber-900/50 dark:to-emerald-800/50">
                    <TestimonialsSection testimonials={testimonials} />
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mt-8 text-center"
                    >
                        <Button
                            asChild
                            className="bg-gradient-to-r from-emerald-600 to-amber-600 text-white transition-all hover:from-emerald-700 hover:to-amber-700"
                        >
                            <Link href={route('testimonials.create')}>
                                Share Your Experience
                            </Link>
                        </Button>
                    </motion.div>
                </Section>
            )}

            {/* Bottom CTAs */}
            <PageCtas ctas={bottomCtas} className="mt-8" />
        </Layout>
    );
};

export default Home;
